import React from 'react';
import { Icon, IconName } from './Icon';
import '../../../styles/components/input.css';
import { MonkeyCursor } from './MonkeyCursor';
import { MonkeyProps } from '../types/monkey.types';
import { SecurityUtils } from '../../../utils/security';

export interface InputProps extends MonkeyProps {
  // Props essentielles
  id: string;
  name: string;
  value: string;
  onChange: (value: string) => void;

  // Props optionnelles
  label?: string;
  placeholder?: string;
  helper?: string;
  error?: string;
  icon?: IconName;
  type?: 'text' | 'email' | 'password' | 'search' | 'tel' | 'url';
  required?: boolean;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  size?: 'sm' | 'md' | 'lg';
}

export const Input: React.FC<InputProps> = ({
  // Props essentielles
  id,
  name,
  value,
  onChange,
  // Props optionnelles
  label,
  placeholder,
  helper,
  error,
  icon,
  type = 'text',
  required = false,
  disabled = false,
  className = '',
  maxLength,
  size = 'md',
  // Props Monkey avec valeurs par défaut
  showMonkey = false,
  monkeyVariant = 'singeOrange',
  monkeyType = 'walking',
  monkeyLookAt = 'right'
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // ✅ CORRIGER : Utiliser la fonction LIVE pendant la saisie
    const sanitizedValue = SecurityUtils.sanitizeInputLive(e.target.value);
    onChange(sanitizedValue);
  };

  const inputClasses = [
    'input-field',
    error ? 'has-error' : '',
    disabled ? 'is-disabled' : '',
    icon ? 'has-icon' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className="input-container">
      {label && (
        <label htmlFor={id} className="input-label">
          {label}
          {required && <span className="required-mark">*</span>}
        </label>
      )}

      <div className="input-wrapper">
        <MonkeyCursor
          showMonkey={showMonkey}
          monkeyVariant={monkeyVariant}
          monkeyType={monkeyType}
          monkeyLookAt={monkeyLookAt}
          offset={{ x: 190, y: monkeyType === 'hanging' ? -68 : 5 }}
          size={size}
        />
        
        {icon && (
          <Icon 
            name={icon} 
            size={16} 
            className="input-icon"
            aria-hidden="true"
          />
        )}
       
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          className={inputClasses}
          aria-invalid={!!error}
          aria-describedby={helper ? `${id}-helper` : undefined}
        />
      </div>

      {helper && !error && (
        <p id={`${id}-helper`} className="input-helper">
          {helper}
        </p>
      )}
      
      {error && (
        <p id={`${id}-error`} className="input-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default Input; 